import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Fade, Slide, Zoom } from "react-awesome-reveal";
import {
  FaCalendarAlt,
  FaChevronRight,
  FaExternalLinkAlt,
  FaImage,
  FaMedal,
  FaTimes,
  FaTrophy,
} from "react-icons/fa";

import { getAdherentsByUser } from "../../services/adherentServices";
import {
  getPalmaresByAdherentId,
  getHomeDemoPalmares,
} from "../../services/palmaresServices";

const MAX_PALMARES_HOME = 3;

const PALMARES_STYLES = [
  {
    gradient: "from-amber-500 via-orange-600 to-slate-950",
    soft: "from-amber-500/15 via-orange-500/10 to-transparent",
    chip: "bg-amber-100 text-amber-800 dark:bg-amber-500/10 dark:text-amber-200",
    accent: "text-amber-600 dark:text-amber-300",
  },
  {
    gradient: "from-sky-600 via-blue-700 to-slate-950",
    soft: "from-sky-500/15 via-blue-500/10 to-transparent",
    chip: "bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200",
    accent: "text-sky-600 dark:text-sky-300",
  },
  {
    gradient: "from-emerald-600 via-teal-700 to-slate-950",
    soft: "from-emerald-500/15 via-teal-500/10 to-transparent",
    chip:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200",
    accent: "text-emerald-600 dark:text-emerald-300",
  },
];

const normalizeAdherentsResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.adherents)) return response.adherents;
  if (Array.isArray(response?.Adherents)) return response.Adherents;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.adherents)) return response.data.adherents;
  return [];
};

const normalizePalmaresResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Palmares)) return response.Palmares;
  if (Array.isArray(response?.palmares)) return response.palmares;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.Palmares)) return response.data.Palmares;
  if (Array.isArray(response?.data?.palmares)) return response.data.palmares;
  return [];
};

const cleanText = (value) => {
  if (!value) return "";

  return String(value)
    .replace(/<[^>]*>/g, "")
    .trim();
};

const getPalmaresDate = (item) =>
  item?.date_palmares || item?.date || item?.createdAt;

const isValidDate = (dateValue) => {
  const date = new Date(dateValue);
  return Number.isFinite(date.getTime());
};

const getTimestamp = (dateValue) => {
  const date = new Date(dateValue);
  return Number.isFinite(date.getTime()) ? date.getTime() : 0;
};

const getAdherentLabel = (item) => {
  const adherent = item?._homeAdherent;

  if (!adherent?.prenom && !adherent?.nom) return "";

  return [adherent.prenom, adherent.nom].filter(Boolean).join(" ");
};

const getAdherentOptionLabel = (adherent) => {
  if (!adherent?.prenom && !adherent?.nom) {
    return `Adhérent #${adherent?.id || ""}`;
  }

  return [adherent.prenom, adherent.nom].filter(Boolean).join(" ");
};

const getLatestPalmares = (items) =>
  [...items]
    .filter((item) => isValidDate(getPalmaresDate(item)))
    .sort(
      (a, b) =>
        getTimestamp(getPalmaresDate(b)) - getTimestamp(getPalmaresDate(a)),
    )
    .slice(0, MAX_PALMARES_HOME);

const dedupePalmares = (items) => {
  const map = new Map();

  items.forEach((item) => {
    const key =
      item?.id ||
      `${item?.titre_palmares}-${item?.adherent_palmares_id}-${getPalmaresDate(
        item,
      )}`;

    if (key && !map.has(key)) {
      map.set(key, item);
    }
  });

  return Array.from(map.values());
};

const formatDate = (dateValue) => {
  if (!isValidDate(dateValue)) return "Date non précisée";

  return new Date(dateValue).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const formatDay = (dateValue) => {
  if (!isValidDate(dateValue)) return "--";

  return new Date(dateValue).toLocaleDateString("fr-FR", {
    day: "2-digit",
  });
};

const formatMonth = (dateValue) => {
  if (!isValidDate(dateValue)) return "---";

  return new Date(dateValue).toLocaleDateString("fr-FR", {
    month: "short",
  });
};

const normalizeClassement = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

const getImageForPlace = (item) => {
  const place = normalizeClassement(item?.classement_palmares);

  if (
    place === "1èr(e)" ||
    place === "1er" ||
    place === "1ère" ||
    place === "1eme" ||
    place === "1ème"
  ) {
    return "/img/palmares/1er-prix.png";
  }

  if (place === "2ème" || place === "2eme") {
    return "/img/palmares/2eme-prix.png";
  }

  if (place === "3ème" || place === "3eme") {
    return "/img/palmares/3eme-prix.png";
  }

  if (place === "4ème" || place === "4eme") {
    return "/img/palmares/4eme-prix.png";
  }

  if (place === "5ème" || place === "5eme") {
    return "/img/palmares/5eme-prix.png";
  }

  if (place === "7ème" || place === "7eme") {
    return "/img/palmares/7eme-prix.png";
  }

  if (place === "9ème" || place === "9eme") {
    return "/img/palmares/9eme-prix.png";
  }

  return "/img/palmares/nc-prix.png";
};

function PalmaresSkeleton() {
  return (
    <div className="grid gap-5 lg:grid-cols-3">
      {[1, 2, 3].map((item) => (
        <div
          key={item}
          className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-3 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20"
        >
          <div className="h-52 animate-pulse rounded-[1.5rem] bg-slate-200 dark:bg-white/10" />

          <div className="space-y-3 p-3 pt-5">
            <div className="h-4 w-24 animate-pulse rounded-full bg-slate-200 dark:bg-white/10" />
            <div className="h-5 w-4/5 animate-pulse rounded-full bg-slate-200 dark:bg-white/10" />
            <div className="h-4 w-2/3 animate-pulse rounded-full bg-slate-200 dark:bg-white/10" />
            <div className="h-20 w-full animate-pulse rounded-2xl bg-slate-200 dark:bg-white/10" />
          </div>
        </div>
      ))}
    </div>
  );
}

function AdherentSelect({
  isConnected,
  adherents,
  selectedAdherentId,
  onChange,
}) {
  if (!isConnected || adherents.length <= 1) return null;

  return (
    <Fade direction="up" triggerOnce delay={180}>
      <div className="rounded-[1.4rem] border border-slate-200 bg-white p-3 shadow-sm dark:border-white/10 dark:bg-white/[0.06]">
        <label
          htmlFor="palmares-adherent"
          className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400"
        >
          Judoka affiché
        </label>

        <select
          id="palmares-adherent"
          value={selectedAdherentId}
          onChange={(event) => onChange(event.target.value)}
          className="min-h-11 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-bold text-slate-800 outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-500/10 dark:border-white/10 dark:bg-slate-950 dark:text-white"
        >
          {adherents.map((adherent) => (
            <option key={adherent.id} value={String(adherent.id)}>
              {getAdherentOptionLabel(adherent)}
            </option>
          ))}
        </select>
      </div>
    </Fade>
  );
}

function PalmaresEmptyState({ isConnected, onAction, hasAdherents }) {
  return (
    <Fade direction="up" triggerOnce>
      <div className="overflow-hidden rounded-[2.2rem] border border-slate-200 bg-white p-3 shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
        <div className="grid gap-5 rounded-[1.8rem] bg-slate-50 p-4 dark:bg-white/[0.04] md:grid-cols-[0.85fr_1.15fr] md:items-center md:p-5">
          <div className="relative min-h-64 overflow-hidden rounded-[1.55rem] bg-slate-900">
            <img
              src="/img/cover/cover_palmares.webp"
              alt="Palmarès Judo Coach Pro"
              className="absolute inset-0 h-full w-full object-cover opacity-80 transition duration-700 hover:scale-105"
            />

            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/35 to-transparent" />

            <div className="absolute bottom-4 left-4 right-4 text-white">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] backdrop-blur-xl">
                <FaTrophy />
                Palmarès
              </div>

              <p className="mt-3 font-heading text-2xl font-bold leading-tight">
                {isConnected
                  ? hasAdherents
                    ? "Le palmarès de ce judoka apparaîtra ici"
                    : "Aucun judoka associé au compte"
                  : "Aucun palmarès de démonstration"}
              </p>
            </div>
          </div>

          <div className="px-1 py-2 md:px-3">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 dark:text-amber-300">
              {isConnected ? "Aucun podium" : "Compte démo"}
            </p>

            <h3 className="mt-3 font-heading text-2xl font-bold leading-tight text-slate-950 dark:text-white sm:text-3xl">
              {isConnected
                ? hasAdherents
                  ? "Les derniers podiums de ce judoka seront mis en avant automatiquement."
                  : "Ajoutez d’abord un adhérent pour suivre son palmarès."
                : "Ajoutez quelques éléments de palmarès au compte démo pour valoriser cette section."}
            </h3>

            <p className="mt-4 max-w-xl text-sm font-medium leading-7 text-slate-600 dark:text-white/55 sm:text-base">
              Photos, classements et tableaux permettent de garder une vraie
              trace du parcours sportif.
            </p>

            <button
              type="button"
              onClick={onAction}
              className="mt-6 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-3 text-sm font-bold text-white shadow-lg font-ui shadow-orange-500/20 transition hover:-translate-y-0.5 active:translate-y-0 sm:w-auto"
            >
              {isConnected ? "Voir mon palmarès" : "Créer mon compte"}
              <FaChevronRight className="text-xs" />
            </button>
          </div>
        </div>
      </div>
    </Fade>
  );
}

function PalmaresCard({ item, index, onImageClick }) {
  const date = getPalmaresDate(item);
  const style = PALMARES_STYLES[index % PALMARES_STYLES.length];

  const title = cleanText(item?.titre_palmares) || "Palmarès sans titre";
  const classement = cleanText(item?.classement_palmares);
  const resultats = cleanText(item?.resultats_palmares);
  const observations = cleanText(item?.observations_palmares);
  const adherentLabel = getAdherentLabel(item);
  const podiumImage = item?.podium_palmares;
  const medalImage = getImageForPlace(item);

  return (
    <Zoom triggerOnce delay={index * 80} className="h-full">
      <article className="group relative h-full overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-3 shadow-xl shadow-slate-200/60 transition duration-300 hover:-translate-y-1 hover:shadow-2xl dark:border-white/10 dark:bg-white/[0.05] dark:shadow-black/20">
        <div
          className={`pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-br ${style.soft}`}
        />

        <div
          className={`relative h-56 overflow-hidden rounded-[1.55rem] bg-gradient-to-br ${style.gradient}`}
        >
          {podiumImage ? (
            <button
              type="button"
              onClick={() => onImageClick(podiumImage)}
              className="absolute inset-0 block h-full w-full"
              aria-label="Agrandir l'image du podium"
            >
              <img
                src={podiumImage}
                alt={title}
                className="h-full w-full object-cover opacity-85 transition duration-700 group-hover:scale-105"
                loading="lazy"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent" />
            </button>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.24),transparent_55%)]" />

              <img
                src={medalImage}
                alt={classement || "Classement"}
                className="relative h-36 w-36 object-contain drop-shadow-2xl transition duration-700 group-hover:scale-110"
                loading="lazy"
              />
            </div>
          )}

          {podiumImage && (
            <div className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-2xl border border-white/20 bg-white/15 text-white backdrop-blur-xl">
              <FaImage />
            </div>
          )}

          <div className="absolute left-4 top-4 overflow-hidden rounded-2xl bg-white text-center shadow-lg dark:bg-slate-950">
            <div
              className={`bg-gradient-to-br ${style.gradient} px-4 py-2 text-white`}
            >
              <p className="text-2xl font-bold leading-none">
                {formatDay(date)}
              </p>

              <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em]">
                {formatMonth(date)}
              </p>
            </div>
          </div>

          <img
            src={medalImage}
            alt={classement || "Classement"}
            className="absolute bottom-4 right-4 h-16 w-16 rounded-full bg-white/90 object-contain p-1 shadow-xl ring-4 ring-white/20"
            loading="lazy"
          />

          <div className="absolute bottom-4 left-4 right-24 text-white">
            <div className="inline-flex max-w-full items-center gap-2 rounded-full bg-white/15 px-3 py-2 text-xs font-bold backdrop-blur-xl">
              <FaMedal className="shrink-0" />
              <span className="truncate">{classement || "Classement NC"}</span>
            </div>
          </div>
        </div>

        <div className="relative p-3 pt-5">
          <div className="flex flex-wrap gap-2">
            {classement && (
              <span
                className={`rounded-full px-3 py-1 text-xs font-bold ${style.chip}`}
              >
                {classement}
              </span>
            )}

            {resultats && (
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-white/60">
                {resultats}
              </span>
            )}
          </div>

          <h3 className="mt-4 line-clamp-2 font-heading text-xl font-bold leading-tight text-slate-950 dark:text-white">
            {title}
          </h3>

          {adherentLabel && (
            <p className="mt-2 text-sm font-bold text-sky-700 dark:text-sky-200">
              {adherentLabel}
            </p>
          )}

          <p className="mt-4 flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-white/55">
            <FaCalendarAlt className={`shrink-0 ${style.accent}`} />
            <span>{formatDate(date)}</span>
          </p>

          {observations && (
            <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-400/20 dark:bg-amber-500/10">
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-amber-700 dark:text-amber-200">
                Souvenir / observation
              </p>

              <p className="mt-2 line-clamp-3 text-sm font-medium leading-6 text-amber-950 dark:text-amber-50">
                {observations}
              </p>
            </div>
          )}

          {(item?.podium_palmares || item?.tableau_palmares) && (
            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              {item?.podium_palmares && (
                <button
                  type="button"
                  onClick={() => onImageClick(item.podium_palmares)}
                  className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-white/10 dark:bg-white/[0.07] dark:text-white dark:hover:bg-white/10"
                >
                  Podium
                  <FaImage className="text-xs" />
                </button>
              )}

              {item?.tableau_palmares && (
                <a
                  href={item.tableau_palmares}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-white/10 dark:bg-white/[0.07] dark:text-white dark:hover:bg-white/10"
                >
                  Tableau
                  <FaExternalLinkAlt className="text-xs" />
                </a>
              )}
            </div>
          )}
        </div>
      </article>
    </Zoom>
  );
}

function ImagePreviewModal({ imageUrl, onClose }) {
  if (!imageUrl) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <Fade direction="up" duration={250} triggerOnce>
        <div className="relative max-h-[90vh] w-full max-w-5xl">
          <button
            type="button"
            onClick={onClose}
            className="absolute -right-2 -top-2 z-10 flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-slate-950 shadow-xl transition hover:scale-105 active:scale-95"
            aria-label="Fermer l'image"
          >
            <FaTimes />
          </button>

          <img
            src={imageUrl}
            alt="Podium agrandi"
            className="max-h-[90vh] w-full rounded-[1.5rem] object-contain shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      </Fade>
    </div>
  );
}

function RecentPalmares({ isConnected }) {
  const navigate = useNavigate();

  const [managedAdherents, setManagedAdherents] = useState([]);
  const [selectedAdherentId, setSelectedAdherentId] = useState("");
  const [adherentsLoaded, setAdherentsLoaded] = useState(!isConnected);

  const [palmares, setPalmares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  const selectedAdherent = useMemo(() => {
    if (!selectedAdherentId) return null;

    return (
      managedAdherents.find(
        (adherent) => String(adherent.id) === String(selectedAdherentId),
      ) || null
    );
  }, [managedAdherents, selectedAdherentId]);

  const sectionContent = useMemo(() => {
    if (isConnected) {
      return {
        eyebrow: "Mon palmarès",
        title: "Les derniers podiums du judoka.",
        subtitle:
          "Ce composant affiche le premier judoka géré. Vous pouvez basculer sur un autre profil.",
        note: null,
        primaryLabel: "Voir tout mon palmarès",
        primaryPath: "/palmares",
      };
    }

    return {
      eyebrow: "Palmarès digital",
      title: "Chaque podium raconte une progression.",
      subtitle:
        "Judo Coach Pro valorise le parcours du judoka avec ses podiums, ses classements et ses souvenirs de compétition.",
      note: "Démonstration : les palmarès ci-dessous proviennent du compte de démonstration.",
      primaryLabel: "Créer mon compte gratuit",
      primaryPath: "/register",
    };
  }, [isConnected]);

  const handleNavigation = () => {
    navigate(sectionContent.primaryPath);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchManagedAdherents = async () => {
      if (!isConnected) {
        if (isMounted) {
          setManagedAdherents([]);
          setSelectedAdherentId("");
          setAdherentsLoaded(true);
        }

        return;
      }

      setAdherentsLoaded(false);

      try {
        const response = await getAdherentsByUser();
        const adherents = normalizeAdherentsResponse(response);

        if (!isMounted) return;

        setManagedAdherents(adherents);

        setSelectedAdherentId((currentId) => {
          const currentStillExists = adherents.some(
            (adherent) => String(adherent.id) === String(currentId),
          );

          if (currentId && currentStillExists) {
            return currentId;
          }

          return adherents[0]?.id ? String(adherents[0].id) : "";
        });
      } catch (error) {
        console.error("Erreur lors de la récupération des adhérents :", error);

        if (isMounted) {
          setManagedAdherents([]);
          setSelectedAdherentId("");
        }
      } finally {
        if (isMounted) {
          setAdherentsLoaded(true);
        }
      }
    };

    fetchManagedAdherents();

    return () => {
      isMounted = false;
    };
  }, [isConnected]);

  useEffect(() => {
    let isMounted = true;

    const fetchSelectedAdherentPalmares = async () => {
      if (!selectedAdherentId) return [];

      const response = await getPalmaresByAdherentId(selectedAdherentId);
      const items = normalizePalmaresResponse(response);

      return dedupePalmares(
        items.map((item) => ({
          ...item,
          _homeAdherent: selectedAdherent
            ? {
                id: selectedAdherent.id,
                nom: selectedAdherent.nom,
                prenom: selectedAdherent.prenom,
              }
            : null,
        })),
      );
    };

    const fetchVisitorPalmares = async () => {
      const response = await getHomeDemoPalmares();
      return normalizePalmaresResponse(response);
    };

    const fetchPalmares = async () => {
      if (isConnected && !adherentsLoaded) {
        setLoading(true);
        return;
      }

      if (isConnected && !selectedAdherentId) {
        setPalmares([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        const loadedPalmares = isConnected
          ? await fetchSelectedAdherentPalmares()
          : await fetchVisitorPalmares();

        if (isMounted) {
          setPalmares(getLatestPalmares(loadedPalmares));
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des palmarès :", error);

        if (isMounted) {
          setPalmares([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPalmares();

    return () => {
      isMounted = false;
    };
  }, [isConnected, adherentsLoaded, selectedAdherentId, selectedAdherent]);

  const hasPalmares = palmares.length > 0;
  const hasAdherents = managedAdherents.length > 0;

  return (
    <section className="relative overflow-hidden bg-slate-50 px-4 py-16 text-slate-950 dark:bg-[#07111f] dark:text-white sm:px-6 sm:py-20 lg:px-8">
      <div className="absolute -left-24 bottom-10 h-72 w-72 rounded-full bg-amber-500/10 blur-3xl" />
      <div className="absolute right-0 top-0 h-80 w-80 rounded-full bg-sky-500/10 blur-3xl dark:bg-sky-400/10" />
      <div className="absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-orange-500/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl">
        <div className="mb-9 grid gap-6 lg:grid-cols-[0.68fr_0.32fr] lg:items-end">
          <Slide direction="left" triggerOnce>
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-700 shadow-sm dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-200">
                <FaTrophy />
                {sectionContent.eyebrow}
              </div>

              <h2 className="mt-5 font-heading text-3xl font-bold leading-tight text-slate-950 dark:text-white sm:text-4xl lg:text-5xl">
                {sectionContent.title}
              </h2>

              <p className="mt-4 max-w-2xl text-base font-medium leading-8 text-slate-600 dark:text-white/55 sm:text-lg">
                {sectionContent.subtitle}
              </p>
            </div>
          </Slide>

          <div className="grid gap-3">
            <AdherentSelect
              isConnected={isConnected}
              adherents={managedAdherents}
              selectedAdherentId={selectedAdherentId}
              onChange={setSelectedAdherentId}
            />

            <Fade direction="up" triggerOnce delay={120}>
              <div className="flex lg:justify-end">
                <button
                  type="button"
                  onClick={handleNavigation}
                  className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-500 to-yellow-400 px-6 py-3 text-sm font-bold text-slate-950 shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 active:translate-y-0 sm:w-auto lg:w-full"
                >
                  {sectionContent.primaryLabel}
                  <FaChevronRight className="text-xs" />
                </button>
              </div>
            </Fade>
          </div>
        </div>

        {sectionContent.note && (
          <Fade direction="up" triggerOnce>
            <div className="mb-6 rounded-[1.5rem] border border-orange-200 bg-orange-50 p-4 text-sm font-semibold leading-6 text-orange-800 dark:border-orange-400/20 dark:bg-orange-500/10 dark:text-orange-100">
              {sectionContent.note}
            </div>
          </Fade>
        )}

        {loading && <PalmaresSkeleton />}

        {!loading && !hasPalmares && (
          <PalmaresEmptyState
            isConnected={isConnected}
            hasAdherents={hasAdherents}
            onAction={handleNavigation}
          />
        )}

        {!loading && hasPalmares && (
          <div className="grid gap-5 lg:grid-cols-3">
            {palmares.map((item, index) => (
              <PalmaresCard
                key={item?.id || `${item?.titre_palmares}-${index}`}
                item={item}
                index={index}
                onImageClick={setSelectedImage}
              />
            ))}
          </div>
        )}
      </div>

      <ImagePreviewModal
        imageUrl={selectedImage}
        onClose={() => setSelectedImage(null)}
      />
    </section>
  );
}

export default RecentPalmares;